import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  CodeBlock,
  FormattedDateTime,
  FormattedRelativeTime,
  Icon,
  InlineStack,
  Link,
  List,
  ListItem,
  PlainButton,
  Strong,
  TableTd,
  TableTr,
  Text,
  TextLabel,
  Tooltip,
  TooltipContent,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { downloadExport } from '@/services/db/export/queries';
import type { SqlImportRow } from '@/services/db/sql-import-row/entity';
import {
  sqlImportRowHasErrorFile,
  sqlImportRowIsFailed,
  sqlImportRowIsPending,
  sqlImportRowIsSkipped,
  sqlImportRowStatusDetails,
} from '@/services/db/sql-import-row/entity';
import { transformSqlImportError } from '@/features/sql-sync';
import { generatePath } from '@/services/router/generate-path';
import { formatNumber } from '@/utils/number';
import { isEmptyString } from '@/utils/string';
import { runDatabaseName, runSummaryParts, targetNoun, type TargetNouns } from '../rows';
import { CancelImportModal } from './cancel-import-modal';

const DOWNLOAD_FAILED_MESSAGE = "The error file couldn't be downloaded.";

type RunSummaryProps = {
  row: SqlImportRow;
  targetNouns: TargetNouns;
};

/** The first column: what the run is doing, or what it did. */
function RunSummary({ row, targetNouns }: RunSummaryProps) {
  if (sqlImportRowIsPending(row)) {
    return <>Syncing {targetNouns.plural}…</>;
  }
  if (row.added_rows === 0 && row.updated_rows === 0) {
    return <Text color="subtle">No {targetNouns.plural} were added or updated</Text>;
  }
  const total = row.added_rows + row.updated_rows + row.failed_rows;
  return (
    <>
      {/* Keyed by position: a relationship run labels every count "relationships synced". */}
      {runSummaryParts(row).map(({ separator, count, label }, index) => (
        <span key={index}>
          {separator}
          <Strong>{formatNumber(count)}</Strong> {label}
        </span>
      ))}
      {row.import_type === 'relationship' ? null : ` ${targetNoun(targetNouns, total)}`}
    </>
  );
}

type ErrorFileProps = {
  row: SqlImportRow;
  workspaceId: number;
  isReadOnly: boolean;
  onDownloadStarted: () => void;
};

/** A failed run's rejected rows, exported for download. */
function ErrorFile({ row, workspaceId, isReadOnly, onDownloadStarted }: ErrorFileProps) {
  const snackbars = useSnackbars();
  const [isDownloading, setIsDownloading] = useState(false);
  const download = async () => {
    onDownloadStarted();
    setIsDownloading(true);
    try {
      await downloadExport(workspaceId, String(row.error_export_id));
    } catch (error) {
      snackbars.create({
        id: 'sqlImports.downloadErrorFile.error',
        variant: 'critical',
        message: getUserFacingErrorMessage(error, DOWNLOAD_FAILED_MESSAGE),
      });
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <>
      <TextLabel mt="200" mb="100">
        Details
      </TextLabel>
      <InlineStack alignBlock="center" alignInline="space-between">
        <Box>
          <Text weight="medium">SQL import errors</Text>
          <Text as="small" color="subtle" display="block">
            Exported by system@customer.io
          </Text>
        </Box>
        <Button
          size="sm"
          variant="secondary"
          icon="download"
          isLoading={isDownloading}
          isDisabled={isReadOnly}
          onClick={() => void download()}
          data-test-sql-import-fail-download-file
        >
          Download
        </Button>
      </InlineStack>
    </>
  );
}

type RunTimelineProps = {
  row: SqlImportRow;
  isReadOnly: boolean;
  onCancelRequested: () => void;
};

/** When the run started and how it ended (or that it is still going, with the way to stop it). */
function RunTimeline({ row, isReadOnly, onCancelRequested }: RunTimelineProps) {
  const status = sqlImportRowStatusDetails(row);
  const isFailed = sqlImportRowIsFailed(row);
  if (sqlImportRowIsPending(row)) {
    return (
      <ListItem>
        <Strong weight="medium">Importing</Strong> for the last{' '}
        <FormattedRelativeTime date={new Date(row.created_at * 1000)} shouldUppercaseFirst={false} />…{' '}
        {isReadOnly ? null : (
          <PlainButton onClick={onCancelRequested} data-test-sql-import-cancel-modal-trigger-button>
            Cancel import
          </PlainButton>
        )}
      </ListItem>
    );
  }
  return (
    <>
      <ListItem>
        <Strong weight="medium">{status.label}</Strong>{' '}
        <FormattedDateTime date={(isFailed ? row.created_at : row.ended_at) * 1000} />
      </ListItem>
      {isFailed || sqlImportRowIsSkipped(row) ? (
        <ListItem>
          <Strong weight="medium">Reason</Strong> <Text color="critical">{transformSqlImportError(row.error)}</Text>
        </ListItem>
      ) : null}
    </>
  );
}

type RunSourceProps = {
  row: SqlImportRow;
  workspaceId: number;
  segmentName: string | undefined;
};

/** Where the run read from, the segment it filled, and the query it ran. */
function RunSource({ row, workspaceId, segmentName }: RunSourceProps) {
  const databaseName = runDatabaseName(row);
  return (
    <>
      <TextLabel mt="300" mb="100">
        Source
      </TextLabel>
      <Text>{isEmptyString(databaseName) ? 'Unknown source' : databaseName}</Text>
      {segmentName == null ? null : (
        <>
          <TextLabel mt="200" mb="100">
            Segment
          </TextLabel>
          <Link
            href={generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
              workspaceId: String(workspaceId),
              segmentId: row.segment_id,
            })}
            variant="primary"
            isUnderlined={false}
          >
            {segmentName}
          </Link>
        </>
      )}
      <TextLabel mt="300" mb="100">
        Query
      </TextLabel>
      <CodeBlock
        code={row.query}
        language="sql"
        withCopyButton={false}
        style={{ maxHeight: '256px', overflowY: 'auto' }}
      />
    </>
  );
}

type RunDetailsProps = {
  row: SqlImportRow;
  workspaceId: number;
  segmentName: string | undefined;
  isReadOnly: boolean;
  onCancelRequested: () => void;
};

/** The expanded row: when the run happened and how it ended, its source, segment and query. */
function RunDetails({ row, workspaceId, segmentName, isReadOnly, onCancelRequested }: RunDetailsProps) {
  const { track } = useAnalytics();
  return (
    <Box p="200" data-test-sql-import-row-expanded>
      <TextLabel mb="100">Summary</TextLabel>
      <List styleType="none">
        <ListItem>
          <Strong weight="medium">Created</Strong> <FormattedDateTime date={row.created_at * 1000} />
        </ListItem>
        <RunTimeline row={row} isReadOnly={isReadOnly} onCancelRequested={onCancelRequested} />
      </List>
      {sqlImportRowIsFailed(row) && sqlImportRowHasErrorFile(row) ? (
        <ErrorFile
          row={row}
          workspaceId={workspaceId}
          isReadOnly={isReadOnly}
          onDownloadStarted={() => void track('sqlImports', 'downloadErrorFile', {})}
        />
      ) : null}
      <RunSource row={row} workspaceId={workspaceId} segmentName={segmentName} />
    </Box>
  );
}

type ImportRowProps = {
  row: SqlImportRow;
  workspaceId: number;
  targetNouns: TargetNouns;
  segmentName: string | undefined;
  isExpanded: boolean;
  isReadOnly: boolean;
  isCanceling: boolean;
  onToggle: () => void;
  /** Cancels the run; resolves to whether it went through (a failure is reported by the caller). */
  onCancel: () => Promise<boolean>;
};

/** One run of the import: a summary line that expands into its details. */
export function ImportRow({
  row,
  workspaceId,
  targetNouns,
  segmentName,
  isExpanded,
  isReadOnly,
  isCanceling,
  onToggle,
  onCancel,
}: ImportRowProps) {
  const { track } = useAnalytics();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const status = sqlImportRowStatusDetails(row);

  return (
    <>
      <TableTr
        shouldHighlightOnHover
        cursor="pointer"
        backgroundColor={isExpanded ? 'subtle' : undefined}
        onClick={onToggle}
        aria-expanded={isExpanded}
        data-test-sql-import-row
      >
        <TableTd data-test-sql-import-row-summary>
          <RunSummary row={row} targetNouns={targetNouns} />
        </TableTd>
        <TableTd>
          <Tooltip isDisabled={status.tooltip == null} isCustom={true}>
            {(context) => (
              <>
                <Badge
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                  color={status.color}
                  data-test-sql-import-row-status={row.status}
                >
                  {status.label}
                </Badge>
                <TooltipContent>{status.tooltip}</TooltipContent>
              </>
            )}
          </Tooltip>
        </TableTd>
        <TableTd textAlign="right" data-test-sql-import-row-timestamp>
          <Text as="small" color="subtle">
            <FormattedDateTime date={row.created_at * 1000} />{' '}
            {/* The row itself toggles on click; this button is the keyboard's way in. */}
            <PlainButton
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Hide run details' : 'Show run details'}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
              data-test-sql-import-row-toggle
            >
              <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} />
            </PlainButton>
          </Text>
        </TableTd>
      </TableTr>
      {isExpanded ? (
        <TableTr isBorderless>
          <TableTd colSpan={3} p="0">
            <RunDetails
              row={row}
              workspaceId={workspaceId}
              segmentName={segmentName}
              isReadOnly={isReadOnly}
              onCancelRequested={() => {
                void track('sqlImports', 'cancelImport', {});
                setIsCancelModalOpen(true);
              }}
            />
          </TableTd>
        </TableTr>
      ) : null}
      <CancelImportModal
        isOpen={isCancelModalOpen}
        isCanceling={isCanceling}
        onConfirm={() => {
          void onCancel().then((canceled) => {
            if (canceled) {
              void track('sqlImports', 'cancelImportStarted', {});
              setIsCancelModalOpen(false);
            }
          });
        }}
        onClose={() => {
          void track('sqlImports', 'cancelImportCanceled', {});
          setIsCancelModalOpen(false);
        }}
      />
    </>
  );
}
